import React, {
  useEffect,
  useState,
  useContext,
  useCallback,
  useMemo,
} from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Stack,
  useMediaQuery,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import UnfoldLessIcon from "@mui/icons-material/UnfoldLess";
import DownloadForOfflineOutlinedIcon from "@mui/icons-material/DownloadForOfflineOutlined";
import AutoGraphOutlinedIcon from "@mui/icons-material/AutoGraphOutlined";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import GridViewOutlinedIcon from "@mui/icons-material/GridViewOutlined";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import PygAnaliticoPageHeader, {
  pygToolbarFieldSx,
  pygToolbarControlSx,
  pygToolbarSidebarFullRowSx,
} from "./PygAnaliticoPageHeader";
import { PygToolbarPanel, PygToolbarFilters } from "./PygAnaliticoToolbar";
import { useTheme, alpha } from "@mui/material/styles";
import { jwtDecode } from "jwt-decode";
import { useTranslation } from "react-i18next";
import { SidebarContext } from "../Sidebar/SidebarProvider";
import PygAnaliticoTable from "./PygAnaliticoTable";
import { exportPygPorMarcas, fetchMarcasKpi } from "./pygAnaliticoExport";
import {
  PYG_VISTA_OPTIONS,
  DEFAULT_PYG_VISTA,
  normalizePygVista,
  getPygVistaApiParams,
  getPygFiltroResumen,
  buildPygMarcasUrl,
  pygAnaliticoBasePath,
  resolvePygVista,
  isNmlSpainPygVista,
  canAccessPygVista,
  pygVistaFallbackPath,
  NML_BALANCE_PATH,
} from "../../constants/pygEmpresas";
import { getUserById } from "../../api/User/userApi";
import { mapUserToPermissionState } from "../../utils/userPermissionsUtils";
import {
  pygVistaLabelKey,
  pygFiltroResumenKey,
} from "../../constants/pygLineI18n";
import MarcaKpiChips from "./MarcaKpiChips";
import AnaliticoMarcasCharts from "./AnaliticoMarcasCharts";
import { scoreMarcaInteres } from "./marcaInteresUtils";
import { marcasForVista } from "./marcaRollupUtils";
import { pageContentCardSx } from "../../styles/pageModernStyles";
import { authFetch } from "../../api/authFetch";

const API_URL = process.env.REACT_APP_API_URL;

const AnaliticoCard = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const { contentMargin } = useContext(SidebarContext);
  const { empresa: empresaParam } = useParams();
  const [year, setYear] = useState(new Date().getFullYear());
  const pygVista = normalizePygVista(empresaParam);
  const vistaConfig = resolvePygVista(pygVista);
  const vistaApi = getPygVistaApiParams(pygVista);
  const accent = theme.palette.primary.main;
  const pageGradient =
    theme.palette.mode === "dark"
      ? `linear-gradient(180deg, ${theme.palette.background.default} 0%, ${alpha(theme.palette.primary.main, 0.08)} 100%)`
      : "linear-gradient(180deg, #f8fafc 0%, #eef6f8 45%, #f5f3ff 100%)";
  const filtroResumen = t(pygFiltroResumenKey(pygVista), {
    defaultValue: getPygFiltroResumen(pygVista),
  });
  const vistaLabel = t(pygVistaLabelKey(pygVista), {
    defaultValue: vistaConfig.label,
  });
  const lockVista = isNmlSpainPygVista(pygVista);
  const onVistaChange = (nextVista) => {
    navigate(pygAnaliticoBasePath(nextVista, true));
  };

  useEffect(() => {
    if (lockVista) {
      navigate(pygAnaliticoBasePath("nml_spain"), { replace: true });
      return;
    }
    if (!empresaParam) {
      navigate(pygAnaliticoBasePath(DEFAULT_PYG_VISTA, true), {
        replace: true,
      });
    }
  }, [empresaParam, lockVista, navigate]);

  const [marcas, setMarcas] = useState([]);
  const [loadingMarcas, setLoadingMarcas] = useState(true);
  const [errorMarcas, setErrorMarcas] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [exportStatus, setExportStatus] = useState("");
  const [expandedMarcas, setExpandedMarcas] = useState({});
  const [marcasKpi, setMarcasKpi] = useState({});
  const [loadingKpi, setLoadingKpi] = useState(false);
  const [selectedMarca, setSelectedMarca] = useState("Todas");

  /** Catálogo API ajustado (p. ej. quitar ZARA MAN genérico si existe ZARA MAN ZERO). */
  const marcasVisibles = useMemo(
    () => marcasForVista(marcas, pygVista),
    [marcas, pygVista],
  );

  const marcasListado = useMemo(() => {
    if (selectedMarca === "Todas") return marcasVisibles;
    return marcasVisibles.filter((m) => m === selectedMarca);
  }, [marcasVisibles, selectedMarca]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return navigate("/login");
    let cancelled = false;
    (async () => {
      try {
        const decoded = jwtDecode(token);
        if (decoded.exp < Date.now() / 1000) {
          localStorage.removeItem("token");
          navigate("/login");
          return;
        }
        const user = await getUserById(decoded.userId);
        if (cancelled) return;
        const perms = mapUserToPermissionState(user);
        if (!canAccessPygVista(perms, pygVista)) {
          navigate(pygVistaFallbackPath(perms, true), { replace: true });
        }
      } catch {
        if (!cancelled) navigate("/login");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate, pygVista]);

  useEffect(() => {
    if (lockVista) return;
    setLoadingMarcas(true);
    setErrorMarcas(null);
    const { empresa, nivel1, omitirGeneralStage } = vistaApi;

    const tryMarcasEndpoint = () =>
      authFetch(buildPygMarcasUrl(API_URL, year, pygVista));

    const trySaldosFallback = () => {
      let url = `${API_URL}/api/pyg/saldos-npgc?year=${year}&empresa=${encodeURIComponent(empresa)}`;
      if (nivel1) url += `&nivel1=${encodeURIComponent(nivel1)}`;
      if (omitirGeneralStage) url += "&omitirGeneralStage=1";
      return authFetch(url)
        .then((res) => {
          if (!res.ok) throw new Error(res.statusText);
          return res.json();
        })
        .then((rows) => {
          const seen = new Set();
          const lista = [];
          (rows || []).forEach((r) => {
            const m = (r.Nivel2Analitica ?? r.nivel2Analitica ?? "")
              .toString()
              .trim();
            if (m && !seen.has(m)) {
              seen.add(m);
              lista.push(m);
            }
          });
          lista.sort();
          return lista;
        });
    };

    tryMarcasEndpoint()
      .then((res) => {
        if (res.ok) return res.json().then((data) => data.marcas || []);
        if (res.status === 404) return trySaldosFallback();
        throw new Error(res.statusText);
      })
      .then((lista) => setMarcas(Array.isArray(lista) ? lista : []))
      .catch((e) => {
        setErrorMarcas(e.message);
        setMarcas([]);
      })
      .finally(() => setLoadingMarcas(false));
  }, [
    year,
    pygVista,
    lockVista,
    vistaApi.empresa,
    vistaApi.nivel1,
    vistaApi.omitirGeneralStage,
  ]);

  useEffect(() => {
    setExpandedMarcas({});
    setSelectedMarca("Todas");
  }, [year, pygVista, marcasVisibles.length]);

  useEffect(() => {
    if (selectedMarca === "Todas") return;
    if (!marcasVisibles.includes(selectedMarca)) {
      setSelectedMarca("Todas");
      return;
    }
    setExpandedMarcas((prev) => ({ ...prev, [selectedMarca]: true }));
    const el = document.getElementById(
      `marca-acc-${encodeURIComponent(selectedMarca)}`,
    );
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [selectedMarca, marcasVisibles]);

  const marcasKey = useMemo(
    () => marcasVisibles.join("\u0001"),
    [marcasVisibles],
  );

  useEffect(() => {
    if (!marcasKey) {
      setMarcasKpi({});
      return;
    }
    const { empresa, nivel1, omitirGeneralStage } = vistaApi;
    const listaMarcas = marcasKey.split("\u0001").filter(Boolean);

    setLoadingKpi(true);
    fetchMarcasKpi(
      API_URL,
      empresa,
      year,
      nivel1,
      listaMarcas,
      setExportStatus,
      omitirGeneralStage,
      t,
    )
      .then(setMarcasKpi)
      .catch(() => setMarcasKpi({}))
      .finally(() => setLoadingKpi(false));
  }, [
    year,
    pygVista,
    vistaApi.empresa,
    vistaApi.nivel1,
    vistaApi.omitirGeneralStage,
    marcasKey,
    t,
  ]);

  const handleAccordionChange = useCallback(
    (marca) => (_event, isExpanded) => {
      setExpandedMarcas((prev) => ({ ...prev, [marca]: isExpanded }));
    },
    [],
  );

  const expandAll = () => {
    const next = {};
    marcasListado.forEach((m) => {
      next[m] = true;
    });
    setExpandedMarcas(next);
  };

  const collapseAll = () => setExpandedMarcas({});

  const handleSelectMarcaFromChart = useCallback((marca) => {
    setSelectedMarca(marca);
    setExpandedMarcas((prev) => ({ ...prev, [marca]: true }));
    const el = document.getElementById(
      `marca-acc-${encodeURIComponent(marca)}`,
    );
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, []);

  const expandedCount = marcasListado.filter((m) => expandedMarcas[m]).length;

  const handleExportPorMarcas = async () => {
    setExporting(true);
    setExportStatus(t("pygAnalitico.exportPreparingBrandSheets"));
    try {
      const { empresa, nivel1, omitirGeneralStage } = vistaApi;
      const count = await exportPygPorMarcas({
        apiUrl: API_URL,
        empresa,
        year,
        nivel1,
        omitirGeneralStage,
        marcas: marcasVisibles,
        t,
        onProgress: setExportStatus,
      });
      setExportStatus(t("pygAnalitico.exportDoneSheets", { count }));
    } catch (e) {
      setExportStatus(e.message || t("pygAnalitico.errorExport"));
    } finally {
      setExporting(false);
    }
  };

  if (lockVista) return null;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100dvh",
        maxHeight: "100dvh",
        boxSizing: "border-box",
        overflow: "auto",
        WebkitOverflowScrolling: "touch",
        background: pageGradient,
        pt: { xs: 1, md: 1.25 },
        px: { xs: 1.25, sm: 2 },
        pr: { xs: 1.25, sm: 2, md: 3 },
        pb: { xs: 10, md: 1.5 },
        ...contentMargin,
        minWidth: 0,
        width: "100%",
      }}
    >
      <Box sx={{ flexShrink: 0 }}>
        <PygAnaliticoPageHeader
          sticky={!isMobile}
          compact
          eyebrow={
            lockVista
              ? t("pygAnalitico.eyebrowNml")
              : t("pygAnalitico.eyebrowFinance")
          }
          title={t("pygAnalitico.titleByBrand", { year })}
          subtitle={t("pygAnalitico.subtitleByBrand")}
          filtroResumen={filtroResumen}
          exportStatus={exportStatus}
          kpiChips={
            <Stack direction="row" spacing={0.7} useFlexGap flexWrap="wrap">
              <Chip
                size="small"
                icon={<AutoGraphOutlinedIcon sx={{ fontSize: 16 }} />}
                label={t("pygAnalitico.detail.year", { year })}
                sx={{
                  fontWeight: 700,
                  bgcolor: theme.palette.background.paper,
                  border: "1px solid",
                  borderColor: theme.palette.divider,
                }}
              />
              <Chip
                size="small"
                variant="outlined"
                label={vistaLabel}
                sx={{ fontWeight: 700 }}
              />
              {!loadingMarcas && marcasVisibles.length > 0 && (
                <Chip
                  size="small"
                  label={t("pygAnalitico.brandsCount", {
                    count: marcasVisibles.length,
                  })}
                  sx={{
                    fontWeight: 700,
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    color: "primary.dark",
                    border: "1px solid",
                    borderColor: alpha(theme.palette.primary.main, 0.25),
                  }}
                />
              )}
            </Stack>
          }
          navChips={
            <>
              <Chip
                size="small"
                icon={<TableChartOutlinedIcon sx={{ fontSize: 16 }} />}
                label={t("pygAnalitico.tableGlobal")}
                variant="outlined"
                clickable
                onClick={() => navigate(pygAnaliticoBasePath(pygVista, false))}
                sx={{ fontWeight: 700 }}
              />
              <Chip
                size="small"
                icon={<GridViewOutlinedIcon sx={{ fontSize: 16 }} />}
                label={t("pygAnalitico.byBrand")}
                color="primary"
                sx={{ fontWeight: 700 }}
              />
              {lockVista ? (
                <Chip
                  size="small"
                  icon={<AccountBalanceOutlinedIcon sx={{ fontSize: 16 }} />}
                  label={t("pygAnalitico.balance.navBalance")}
                  variant="outlined"
                  clickable
                  onClick={() => navigate(NML_BALANCE_PATH)}
                  sx={{ fontWeight: 700 }}
                />
              ) : null}
            </>
          }
          toolbar={
            <PygToolbarPanel variant="sidebar">
              <PygToolbarFilters variant="sidebar">
                {!lockVista && (
                  <FormControl
                    size="small"
                    fullWidth
                    sx={{
                      ...pygToolbarControlSx,
                      ...pygToolbarSidebarFullRowSx,
                    }}
                  >
                    <InputLabel>{t("pygAnalitico.view")}</InputLabel>
                    <Select
                      value={pygVista}
                      label={t("pygAnalitico.view")}
                      onChange={(e) => onVistaChange(String(e.target.value))}
                      sx={pygToolbarFieldSx(theme)}
                    >
                      {PYG_VISTA_OPTIONS.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {t(pygVistaLabelKey(opt.value), {
                            defaultValue: opt.label,
                          })}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
                <FormControl size="small" fullWidth sx={pygToolbarControlSx}>
                  <InputLabel>{t("pygAnalitico.year")}</InputLabel>
                  <Select
                    value={year}
                    label={t("pygAnalitico.year")}
                    onChange={(e) => setYear(Number(e.target.value))}
                    sx={pygToolbarFieldSx(theme)}
                  >
                    {[2023, 2024, 2025, 2026].map((y) => (
                      <MenuItem key={y} value={y}>
                        {y}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl
                  size="small"
                  fullWidth
                  disabled={loadingMarcas}
                  sx={pygToolbarControlSx}
                >
                  <InputLabel>{t("pygAnalitico.brand")}</InputLabel>
                  <Select
                    value={selectedMarca}
                    label={t("pygAnalitico.brand")}
                    onChange={(e) => setSelectedMarca(String(e.target.value))}
                    sx={pygToolbarFieldSx(theme)}
                  >
                    <MenuItem value="Todas">
                      {t("pygAnalitico.allBrands")}
                    </MenuItem>
                    {marcasVisibles.map((m) => (
                      <MenuItem key={m} value={m}>
                        {m}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </PygToolbarFilters>
              <Button
                variant="outlined"
                disabled={
                  exporting || loadingMarcas || marcasVisibles.length === 0
                }
                onClick={handleExportPorMarcas}
                startIcon={
                  exporting ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    <DownloadForOfflineOutlinedIcon />
                  )
                }
                sx={{
                  height: 40,
                  width: "100%",
                  flexShrink: 0,
                  fontWeight: 700,
                  borderRadius: 1.5,
                  px: 1.75,
                  whiteSpace: "nowrap",
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                  borderColor: alpha(theme.palette.primary.main, 0.35),
                  color: "primary.dark",
                }}
              >
                {t("pygAnalitico.exportExcel")}
              </Button>
            </PygToolbarPanel>
          }
        />
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1.25,
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            flexShrink: 0,
            ...pageContentCardSx(theme, accent),
            p: { xs: 1.25, sm: 1.5 },
          }}
        >
          <AnaliticoMarcasCharts
            marcas={marcasListado}
            marcasKpi={marcasKpi}
            loadingKpi={loadingKpi}
            year={year}
            onSelectMarca={handleSelectMarcaFromChart}
          />
        </Box>

        {errorMarcas && (
          <Typography color="error" sx={{ mb: 1, flexShrink: 0 }}>
            {t("pygAnalitico.errorLoadBrands", { error: errorMarcas })}
          </Typography>
        )}

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
          }}
        >
          {loadingMarcas ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 3 }}>
              <CircularProgress size={24} />
              <Typography color="text.secondary">
                {t("pygAnalitico.loadingBrands")}
              </Typography>
            </Box>
          ) : marcasVisibles.length === 0 ? (
            <Typography color="text.secondary">
              {t("pygAnalitico.noBrandsFilter")}
            </Typography>
          ) : (
            <Box
              sx={{
                ...pageContentCardSx(theme, accent),
                p: { xs: 1.25, sm: 1.5 },
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ mb: 1.5, flexWrap: "wrap" }}
              >
                <Typography variant="body2" color="text.secondary">
                  {selectedMarca === "Todas"
                    ? t("pygAnalitico.brandsCount", {
                        count: marcasListado.length,
                      })
                    : t("pygAnalitico.brandNamed", { brand: selectedMarca })}
                  {expandedCount > 0
                    ? ` · ${t("pygAnalitico.openAccordions", { count: expandedCount })}`
                    : ` · ${t("pygAnalitico.allCollapsed")}`}
                </Typography>
                <Button
                  size="small"
                  startIcon={<UnfoldMoreIcon />}
                  onClick={expandAll}
                  sx={{ textTransform: "none" }}
                >
                  {t("pygAnalitico.expandAll")}
                </Button>
                <Button
                  size="small"
                  startIcon={<UnfoldLessIcon />}
                  onClick={collapseAll}
                  sx={{ textTransform: "none" }}
                >
                  {t("pygAnalitico.collapseAll")}
                </Button>
              </Stack>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                {marcasListado.map((marca) => {
                  const isOpen = !!expandedMarcas[marca];
                  return (
                    <Accordion
                      key={marca}
                      id={`marca-acc-${encodeURIComponent(marca)}`}
                      expanded={isOpen}
                      onChange={handleAccordionChange(marca)}
                      disableGutters
                      elevation={0}
                      sx={{
                        border: "1px solid",
                        borderColor: theme.palette.divider,
                        borderRadius: "10px !important",
                        overflow: "hidden",
                        "&:before": { display: "none" },
                        bgcolor: theme.palette.background.paper,
                        boxShadow:
                          theme.palette.mode === "dark"
                            ? "0 4px 12px rgba(0,0,0,0.2)"
                            : "0 4px 12px rgba(15,23,42,0.04)",
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        sx={{
                          minHeight: { xs: 52, sm: 56 },
                          px: { xs: 1.25, sm: 2 },
                          py: 1,
                          bgcolor: theme.palette.background.paper,
                          borderBottom: isOpen ? "1px solid" : "none",
                          borderColor: "divider",
                          "& .MuiAccordionSummary-content": {
                            my: 0.5,
                            width: "100%",
                            flexDirection: "column",
                            gap: 0.75,
                          },
                        }}
                      >
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 700, width: "100%" }}
                        >
                          {marca}
                        </Typography>
                        <MarcaKpiChips
                          kpi={marcasKpi[marca]}
                          loading={loadingKpi}
                          prioridadMeta={
                            marcasKpi[marca]
                              ? scoreMarcaInteres(marcasKpi[marca])
                              : null
                          }
                        />
                      </AccordionSummary>
                      <AccordionDetails
                        sx={{
                          p: 0,
                          borderTop: "1px solid",
                          borderColor: "divider",
                        }}
                      >
                        {isOpen && (
                          <PygAnaliticoTable
                            key={`${marca}-${year}-${pygVista}`}
                            year={year}
                            pygVista={pygVista}
                            nivel2={marca}
                            hideYearSelector
                            embedded
                            compactEmbedded
                          />
                        )}
                      </AccordionDetails>
                    </Accordion>
                  );
                })}
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default AnaliticoCard;
